import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, Stack, Typography, Slider, Select, MenuItem } from '@mui/material';

const PLAYBACK_SPEED_OPTIONS = [
    { label: '0.5x', value: 2000 },
    { label: '1x', value: 1000 },
    { label: '2x', value: 500 },
    { label: '10x', value: 100 },
    { label: '50x', value: 20 },
    { label: '100x', value: 10 }
];

// Intelligently determine step size based on playback speed
function getStepSize(playbackSpeed) {
    if (playbackSpeed >= 1000) return 1;      // 0.5x, 1x
    if (playbackSpeed >= 500) return 2;       // 2x
    if (playbackSpeed >= 100) return 10;      // 10x
    if (playbackSpeed >= 20) return 50;       // 50x
    return 100;                               // 100x
}

export default function PlaybackController({
    length,
    onChange,
    initialIndex = 0,
    initialSpeed = 1000
}) {
    const [playIndex, setPlayIndex] = useState(initialIndex);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playbackSpeed, setPlaybackSpeed] = useState(initialSpeed);
    const playIntervalRef = useRef(null);

    // Notify parent of index change
    useEffect(() => {
        if (onChange) onChange(playIndex);
    }, [playIndex, onChange]);

    // Handle play/pause logic
    useEffect(() => {
        if (isPlaying) {
            playIntervalRef.current = setInterval(() => {
                setPlayIndex(prev => {
                    if (prev >= length - 1) {
                        setIsPlaying(false);
                        clearInterval(playIntervalRef.current);
                        return prev;
                    }
                    const stepSize = getStepSize(playbackSpeed);
                    return Math.min(prev + stepSize, length - 1);
                });
            }, playbackSpeed);
        }
        return () => clearInterval(playIntervalRef.current);
    }, [isPlaying, playbackSpeed, length]);

    const handlePlay = () => {
        if (!isPlaying && length > 0 && playIndex < length - 1) setIsPlaying(true);
    };
    const handlePause = () => setIsPlaying(false);
    const handleReset = () => {
        setIsPlaying(false);
        setPlayIndex(0);
    };
    const handleSliderChange = (_, value) => {
        setPlayIndex(value);
        if (isPlaying) setIsPlaying(false);
    };

    return (
        <Box>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handlePlay}
                    disabled={isPlaying || length === 0 || playIndex >= length - 1}
                >
                    Play
                </Button>
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={handlePause}
                    disabled={!isPlaying}
                >
                    Pause
                </Button>
                <Button
                    variant="outlined"
                    onClick={handleReset}
                    disabled={playIndex === 0 && !isPlaying}
                >
                    Reset
                </Button>
            </Stack>
            <Stack spacing={2} direction="row" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ minWidth: 80 }}>Speed</Typography>
                <Select
                    value={playbackSpeed}
                    onChange={e => setPlaybackSpeed(e.target.value)}
                    size="small"
                    sx={{ width: 100 }}
                >
                    {PLAYBACK_SPEED_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                </Select>
            </Stack>
            <Stack spacing={2} direction="row" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ minWidth: 80 }}>Position</Typography>
                <Slider
                    min={0}
                    max={length > 0 ? length - 1 : 0}
                    step={1}
                    value={playIndex}
                    onChange={handleSliderChange}
                    valueLabelDisplay="auto"
                    sx={{ width: 120 }}
                    disabled={length === 0}
                />
                <Typography
                    variant="body2"
                    sx={{ minWidth: '90px', width: '90px', textAlign: 'right', display: 'inline-block' }}
                >
                    {length > 0 ? `${playIndex + 1} / ${length}` : '0 / 0'}
                </Typography>
            </Stack>
        </Box>
    );
}